import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Form, Radio, Select, Input, InputNumber, DatePicker, TimePicker, Row, Col,
  Button, Space, Typography, Divider, message, Descriptions, Tag, Alert,
} from 'antd';
import {
  PhoneOutlined, UserOutlined, SaveOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { searchEntities, createFollowup } from '../services/callService';
import { fetchSettings } from '../services/settingsService';
import { PRIORITY_COLORS } from './callConstants';

const { Text, Title } = Typography;
const { TextArea } = Input;
const AFTER_UNITS = ['Hours', 'Days', 'Weeks', 'Months', 'Years'];

const NewFollowup = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form] = Form.useForm();
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  const [entityType, setEntityType] = useState('Lead');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);   // selected lead/client summary
  const [searching, setSearching] = useState(false);

  const [callStatus, setCallStatus] = useState(null);    // Connected | Not Connected
  const [notReason, setNotReason] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [nextMode, setNextMode] = useState('date');      // date | after

  const sources = settings; // alias
  const executives = settings.executive || [];
  const callOutcomes = settings.call_outcome || [];
  const statuses = settings.followup_status || [];
  const lostReasons = settings.lost_reason || [];
  const followupTypes = settings.followup_type || [];

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings(); setSettings(s);
      } catch { /* ignore */ }
    })();
    form.setFieldsValue({
      CallType: 'Outgoing', CallAt: dayjs(), EntityType: 'Lead',
    });
    // Preselect from query params (e.g. navigated from a lead)
    const leadId = params.get('leadId');
    const clientId = params.get('clientId');
    if (leadId) { setEntityType('Lead'); form.setFieldValue('EntityType', 'Lead'); preselect('lead', leadId); }
    else if (clientId) { setEntityType('Client'); form.setFieldValue('EntityType', 'Client'); preselect('client', clientId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preselect = async (type, id) => {
    try {
      // search returns lists; fetch by a broad query then match id is overkill — use detail via name search
      const res = await searchEntities(type, id.substring(0, 6));
      const hit = res.find(r => r.id === id) || res[0];
      if (hit) selectEntity(hit);
    } catch { /* ignore */ }
  };

  const doSearch = useMemo(() => {
    let t;
    return (q) => {
      clearTimeout(t);
      if (!q || q.length < 2) { setOptions([]); return; }
      setSearching(true);
      t = setTimeout(async () => {
        try {
          const res = await searchEntities(entityType === 'Client' ? 'client' : 'lead', q);
          setOptions(res);
        } catch { /* ignore */ }
        finally { setSearching(false); }
      }, 300);
    };
  }, [entityType]);

  const selectEntity = (rec) => {
    setSelected(rec);
    form.setFieldsValue({
      LeadID: entityType === 'Lead' ? rec.id : undefined,
      ClientID: entityType === 'Client' ? rec.id : undefined,
    });
  };

  const onEntityTypeChange = (e) => {
    setEntityType(e.target.value);
    setSelected(null); setOptions([]);
    form.setFieldsValue({ LeadID: undefined, ClientID: undefined });
  };

  const computeScheduledAt = (values) => {
    if (callStatus === 'Connected' && (outcome === 'Lost' || outcome === 'Not Interested')) return null;
    if (callStatus === 'Not Connected' && notReason !== 'Call Later') return null;
    if (nextMode === 'after' && values.afterValue && values.afterUnit) {
      let base = dayjs().add(values.afterValue, values.afterUnit.toLowerCase());
      if (values.afterUnit !== 'Hours' && values.nextTime) {
        base = base.hour(values.nextTime.hour()).minute(values.nextTime.minute()).second(0);
      }
      return base.format('YYYY-MM-DD HH:mm:ss');
    }
    if (values.nextDate) {
      let d = values.nextDate;
      if (values.nextTime) d = d.hour(values.nextTime.hour()).minute(values.nextTime.minute()).second(0);
      else d = d.hour(10).minute(0).second(0);
      return d.format('YYYY-MM-DD HH:mm:ss');
    }
    return null;
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch { return; }

    if (entityType === 'Lead' && !values.LeadID) { message.warning('Please select a lead'); return; }
    if (entityType === 'Client' && !values.ClientID) { message.warning('Please select a client'); return; }

    const scheduledAt = computeScheduledAt(values);
    const payload = {
      EntityType: entityType,
      LeadID: values.LeadID || null,
      ClientID: values.ClientID || null,
      InquiryName: values.InquiryName || null,
      InquiryMobile: values.InquiryMobile || null,
      City: values.InquiryCity || (selected && selected.City) || null,
      CallType: values.CallType,
      CallAt: values.CallAt ? values.CallAt.format('YYYY-MM-DD HH:mm:ss') : null,
      Duration: values.Duration || 0,
      ExecutiveName: values.ExecutiveName || null,
      CallStatus: callStatus,
      NotConnectedReason: callStatus === 'Not Connected' ? notReason : null,
      Outcome: callStatus === 'Connected' ? outcome : null,
      LostReason: outcome === 'Lost' ? values.LostReason : null,
      FollowupType: values.FollowupType || null,
      ScheduledAt: scheduledAt,
      Reminder: values.Reminder ? 1 : 0,
      Notes: values.Notes || (outcome === 'Not Interested' ? values.NotInterestedReason : '') || null,
    };

    setSaving(true);
    try {
      await createFollowup(payload);
      message.success('Follow-up saved');
      navigate('/calls');
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to save follow-up');
    } finally { setSaving(false); }
  };

  const requiresNext = callStatus === 'Connected' && outcome &&
    outcome !== 'Lost' && outcome !== 'Not Interested';
  const callLater = callStatus === 'Not Connected' && notReason === 'Call Later';

  const nextScheduler = (
    <>
      <Divider orientation="left" plain>When should the next follow-up be?</Divider>
      <Radio.Group value={nextMode} onChange={(e) => setNextMode(e.target.value)} style={{ marginBottom: 12 }}>
        <Radio.Button value="date">Specific Date</Radio.Button>
        <Radio.Button value="after">After…</Radio.Button>
      </Radio.Group>
      {nextMode === 'date' ? (
        <Row gutter={16}>
          <Col xs={12}><Form.Item name="nextDate" label="Follow-up Date" rules={[{ required: requiresNext || callLater, message: 'Select a date' }]}>
            <DatePicker style={{ width: '100%' }} size="large" format="DD MMM YYYY" /></Form.Item></Col>
          <Col xs={12}><Form.Item name="nextTime" label="Follow-up Time">
            <TimePicker style={{ width: '100%' }} size="large" use12Hours format="hh:mm A" /></Form.Item></Col>
        </Row>
      ) : (
        <Row gutter={16}>
          <Col xs={8}><Form.Item name="afterValue" label="After" rules={[{ required: true, message: 'Number' }]}>
            <InputNumber min={1} style={{ width: '100%' }} size="large" /></Form.Item></Col>
          <Col xs={8}><Form.Item name="afterUnit" label="Unit" rules={[{ required: true, message: 'Unit' }]}>
            <Select size="large" options={AFTER_UNITS.map(u => ({ value: u, label: u }))} /></Form.Item></Col>
          <Col xs={8}><Form.Item name="nextTime" label="Time">
            <TimePicker style={{ width: '100%' }} size="large" use12Hours format="hh:mm A" /></Form.Item></Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col xs={12}><Form.Item name="FollowupType" label="Follow-up Type">
          <Select allowClear size="large" placeholder="Auto from outcome"
            options={followupTypes.map(t => ({ value: t.Value, label: t.Label }))} /></Form.Item></Col>
        <Col xs={12}><Form.Item name="Reminder" label="Reminder" initialValue={true}>
          <Radio.Group><Radio value={true}>On</Radio><Radio value={false}>Off</Radio></Radio.Group></Form.Item></Col>
      </Row>
    </>
  );

  return (
    <div>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/calls')} />
        <div>
          <Title level={4} style={{ margin: 0 }}>New Follow-up</Title>
          <Text type="secondary">Log a call and schedule the next step</Text>
        </div>
      </Space>

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            {/* STEP 1 — Customer */}
            <Card title="1. Customer" bordered={false} style={{ borderRadius: 8, marginBottom: 16 }}>
              <Form.Item name="EntityType" label="Customer Type">
                <Radio.Group onChange={onEntityTypeChange} value={entityType}>
                  <Radio value="Lead">Existing Lead</Radio>
                  <Radio value="Client">Existing Client</Radio>
                  <Radio value="Inquiry">Basic Inquiry</Radio>
                </Radio.Group>
              </Form.Item>

              {entityType !== 'Inquiry' ? (
                <>
                  <Form.Item label={`Search ${entityType}`} required>
                    <Select
                      showSearch filterOption={false} size="large"
                      placeholder={`Type name or mobile…`}
                      notFoundContent={searching ? 'Searching…' : null}
                      onSearch={doSearch}
                      onChange={(val, opt) => selectEntity(opt.record)}
                      options={options.map(o => ({
                        value: o.id, label: `${o.Name} — ${o.Mobile || ''}`, record: o,
                      }))}
                      suffixIcon={<UserOutlined />}
                    />
                  </Form.Item>
                  <Form.Item name="LeadID" hidden><Input /></Form.Item>
                  <Form.Item name="ClientID" hidden><Input /></Form.Item>
                  {selected && (
                    <Descriptions size="small" column={2} bordered style={{ marginBottom: 8 }}>
                      <Descriptions.Item label="Name">{selected.Name}</Descriptions.Item>
                      <Descriptions.Item label="Mobile">{selected.Mobile}</Descriptions.Item>
                      <Descriptions.Item label="City">{selected.City || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Property">{selected.PropertyType || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Priority">
                        {selected.Priority ? <Tag color={PRIORITY_COLORS[selected.Priority] || 'default'}>{selected.Priority}</Tag> : '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Follow-up">
                        {selected.LastFollowup ? dayjs(selected.LastFollowup).format('DD MMM YYYY, hh:mm A') : '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </>
              ) : (
                <Row gutter={16}>
                  <Col xs={24} sm={8}><Form.Item name="InquiryName" label="Name" rules={[{ required: true, message: 'Name' }]}>
                    <Input size="large" /></Form.Item></Col>
                  <Col xs={24} sm={8}><Form.Item name="InquiryMobile" label="Mobile" rules={[{ required: true, message: 'Mobile' }]}>
                    <Input size="large" /></Form.Item></Col>
                  <Col xs={24} sm={8}><Form.Item name="InquiryCity" label="City">
                    <Input size="large" /></Form.Item></Col>
                </Row>
              )}
            </Card>

            {/* STEP 3 — Call Result */}
            <Card title="3. Call Result" bordered={false} style={{ borderRadius: 8 }}>
              <Form.Item label="Result" required>
                <Radio.Group onChange={(e) => { setCallStatus(e.target.value); setOutcome(null); setNotReason(null); }} value={callStatus}>
                  <Radio.Button value="Connected">Connected</Radio.Button>
                  <Radio.Button value="Not Connected">Not Connected</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {callStatus === 'Not Connected' && (
                <>
                  <Form.Item label="Reason" required>
                    <Select size="large" value={notReason} onChange={setNotReason} placeholder="Select reason"
                      options={callOutcomes.map(o => ({ value: o.Value, label: o.Label }))} />
                  </Form.Item>
                  {callLater && nextScheduler}
                </>
              )}

              {callStatus === 'Connected' && (
                <>
                  <Form.Item label="Status" required>
                    <Select size="large" value={outcome} onChange={setOutcome} placeholder="Select status" showSearch
                      options={statuses.map(s => ({ value: s.Value, label: s.Label }))} />
                  </Form.Item>
                  {outcome === 'Lost' && (
                    <Form.Item name="LostReason" label="Lost Reason" rules={[{ required: true, message: 'Select reason' }]}>
                      <Select size="large" options={lostReasons.map(r => ({ value: r.Value, label: r.Label }))} />
                    </Form.Item>
                  )}
                  {outcome === 'Not Interested' && (
                    <Form.Item name="NotInterestedReason" label="Reason (optional)">
                      <TextArea rows={2} />
                    </Form.Item>
                  )}
                  {requiresNext && nextScheduler}
                </>
              )}

              <Form.Item name="Notes" label="Notes" style={{ marginTop: 8 }}>
                <TextArea rows={3} placeholder="Conversation notes…" />
              </Form.Item>
            </Card>
          </Col>

          {/* STEP 2 — Call Information */}
          <Col xs={24} lg={10}>
            <Card title="2. Call Information" bordered={false} style={{ borderRadius: 8, marginBottom: 16 }}>
              <Form.Item name="CallAt" label="Call Date & Time">
                <DatePicker showTime style={{ width: '100%' }} size="large" format="DD MMM YYYY, hh:mm A" use12Hours />
              </Form.Item>
              <Form.Item name="Duration" label="Call Duration (minutes)">
                <InputNumber min={0} style={{ width: '100%' }} size="large" placeholder="e.g. 5" />
              </Form.Item>
              <Form.Item name="ExecutiveName" label="Executive">
                <Select size="large" allowClear showSearch placeholder="Select executive"
                  options={executives.map(x => ({ value: x.Value, label: x.Label }))} />
              </Form.Item>
              <Form.Item name="CallType" label="Call Type">
                <Radio.Group>
                  <Radio.Button value="Incoming"><PhoneOutlined /> Incoming</Radio.Button>
                  <Radio.Button value="Outgoing"><PhoneOutlined /> Outgoing</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Card>

            <Card bordered={false} style={{ borderRadius: 8 }}>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="On save, the system auto-updates lead status, last contact, timeline, calendar, reminders, dashboard and call history." />
              <Button type="primary" size="large" block icon={<SaveOutlined />} loading={saving}
                onClick={handleSubmit} disabled={!callStatus}>
                Save Follow-up
              </Button>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default NewFollowup;
